import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Field, Panel, RiskBadge } from "@/lovable-demo/components/bits";
import { fmtCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/suppliers")({
  head: () => ({
    meta: [
      { title: "Supplier Directory — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Tier 1 and Tier 2 supplier directory with quality contacts, escalation paths and warranty recovery exposure linked to governed problem records.",
      },
      { property: "og:title", content: "Supplier Directory — Broadview Warranty Intelligence" },
      {
        property: "og:description",
        content:
          "Supplier contacts, quality performance and recovery exposure linked to each governed problem record.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SuppliersPage,
});

function SuppliersPage() {
  const { data, suppliers } = useWarranty();

  return (
    <AppShell
      title="Supplier Directory"
      subtitle="Responsible suppliers, quality contacts and warranty recovery exposure"
    >
      <div className="space-y-4">
        {suppliers.map((s) => {
          const linked = data.records.filter(
            (r) => r.supplier === s.name || r.recovery.some((rec) => rec.supplier === s.name),
          );
          const recoveryOpen = linked.reduce(
            (sum, r) =>
              sum +
              r.recovery
                .filter((rec) => rec.supplier === s.name && rec.status !== "recovered")
                .reduce((a, rec) => a + rec.amount, 0),
            0,
          );
          const exposure = linked.reduce((sum, r) => sum + r.financial.exposure, 0);

          return (
            <Panel
              key={s.id}
              title={s.name}
              subtitle={`${s.tier} · ${s.category} · ${s.city}, ${s.country}`}
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field
                  label="Quality contact"
                  value={
                    <>
                      {s.contactName}
                      <span className="block text-[0.68rem] font-normal text-muted-foreground">
                        {s.contactTitle}
                      </span>
                    </>
                  }
                />
                <Field
                  label="Contact details"
                  value={
                    <>
                      <a className="text-primary hover:underline" href={`mailto:${s.email}`}>
                        {s.email}
                      </a>
                      <span className="num block text-[0.68rem] font-normal text-muted-foreground">
                        {s.phone}
                      </span>
                    </>
                  }
                />
                <Field
                  label="Performance"
                  value={
                    <>
                      <span className="num">{s.qualityRating.toFixed(1)}/5</span>
                      <span className="num block text-[0.68rem] font-normal text-muted-foreground">
                        {fmtNumber(s.ppm)} PPM · {s.contractRef}
                      </span>
                    </>
                  }
                />
                <Field
                  label="Open recovery / exposure"
                  value={
                    <>
                      <span className="num">{fmtCurrency(recoveryOpen)}</span>
                      <span className="num block text-[0.68rem] font-normal text-muted-foreground">
                        {fmtCurrency(exposure)} linked exposure
                      </span>
                    </>
                  }
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                SQE owner: {s.sqeOwner} · Escalation: {s.escalationContact} · {s.address}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {linked.length === 0 ? (
                  <span className="text-xs text-muted-foreground">No linked problem records.</span>
                ) : (
                  linked.map((r) => (
                    <Link
                      key={r.id}
                      to="/demo/problem-record/$id"
                      params={{ id: r.id }}
                      className="flex items-center gap-2 rounded border border-border/70 bg-elevated/40 px-2.5 py-1.5 text-xs hover:border-primary/50"
                    >
                      <span className="num font-semibold">{r.code}</span>
                      <span className="max-w-[16rem] truncate text-muted-foreground">
                        {r.title}
                      </span>
                      <RiskBadge risk={r.risk} />
                    </Link>
                  ))
                )}
              </div>
            </Panel>
          );
        })}
      </div>
    </AppShell>
  );
}
